import { apiGet } from './client';
import { resolveGuildByServerSlug } from './guilds';
import type { GuildProgressionOverview, ProgressionObjectiveSummary, ProgressionOverview } from './types';

function unwrapProgression(data: unknown): ProgressionOverview {
  const shaped = data as { progression?: ProgressionOverview; expansions?: ProgressionOverview['expansions'] };
  if (shaped.progression) return shaped.progression;
  return { expansions: shaped.expansions ?? [] };
}

function unwrapGuildProgression(data: unknown): GuildProgressionOverview {
  const shaped = data as {
    progression?: GuildProgressionOverview;
    guildProgression?: GuildProgressionOverview;
    guild?: GuildProgressionOverview['guild'];
    expansions?: GuildProgressionOverview['expansions'];
  };
  if (shaped.guildProgression) return shaped.guildProgression;
  if (shaped.progression) return shaped.progression;
  return { guild: shaped.guild ?? null, expansions: shaped.expansions ?? [] };
}

export async function fetchProgressionOverview(serverId?: number, guildId?: string) {
  const params = new URLSearchParams();
  if (serverId !== undefined) params.set('serverId', String(serverId));
  if (guildId) params.set('guildId', guildId);
  const suffix = params.toString() ? `?${params.toString()}` : '';
  const data = await apiGet<unknown>(`/progression${suffix}`);
  return unwrapProgression(data);
}

export async function fetchObjectives() {
  const data = await apiGet<{ objectives?: ProgressionObjectiveSummary[] } | ProgressionObjectiveSummary[]>('/objectives');
  return Array.isArray(data) ? data : data.objectives ?? [];
}

export async function fetchGuildProgression(guildId: string) {
  const data = await apiGet<unknown>(`/guilds/${encodeURIComponent(guildId)}/progression`);
  return unwrapGuildProgression(data);
}

export async function fetchGuildProgressionByServerSlug(serverSlug: string, guildSlug: string) {
  const guild = await resolveGuildByServerSlug(serverSlug, guildSlug);
  return fetchGuildProgression(guild.id);
}


export async function fetchServerProgressionOverview(serverSlug: string, guildId?: string) {
  const params = new URLSearchParams();
  if (guildId) params.set('guildId', guildId);
  const suffix = params.toString() ? `?${params.toString()}` : '';
  const data = await apiGet<unknown>(`/servers/${encodeURIComponent(serverSlug)}/progression${suffix}`);
  const shaped = data as {
    server?: import('./types').NamedRef;
    progression?: ProgressionOverview;
    expansions?: ProgressionOverview['expansions'];
    raidContent?: { rankingPartitions?: Array<{ rankingPartitionId?: string; id?: string; name: string; slug?: string | null; isActive?: boolean }> };
  };
  return {
    server: shaped.server ?? null,
    progression: shaped.progression ?? { expansions: shaped.expansions ?? [] },
    raidContent: shaped.raidContent ?? null
  };
}
